import React, { useCallback, useEffect, useState } from 'react'
import { LoadingTypes } from '../../../modules/loading/reducer'
import { isLoading } from '../../../utils/loadingState'

import { Grid } from '@mui/material'
import { useStyles } from './AzureAccountRegistration.styles'

import {
  CheckBoxFieldv2,
  ModalConfirmV2,
  ModalSizeVariants,
  AutoCompleteWithMultiSelect,
  SelectV2,
  TextInputV2,
  ButtonV2,
} from '@labrav/react-components'
import { formFields } from '../formFields'
import { actionTypeWrapper } from '../../../utils/actionTypeWrapper'
import {
  disconnectAzureAccount,
  getAzureAccountDetails,
  updateValueOnAccountDetails,
} from '../cloudSettingsAccountActions'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { ProductType } from '../../../types'
import { PartnerType } from '../../../types/partner'
import { AzurePopupConnection } from './AzurePopupConnection'
import { DialogComponent } from '../Dialog/Dialog'
import { validateString } from '../validateString'
import type { CloudSettingsState } from '../cloudSettingsState'

export const AzureAccountRegistration: React.FC<{
  productType: ProductType
  partnerType?: PartnerType
  isAzureFlagEnabled?: boolean
}> = ({
  productType,
  partnerType = PartnerType.User,
  isAzureFlagEnabled = false,
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch() as (action: unknown) => Promise<unknown>
  const [openDisconnectModal, setOpenDisconnectModal] = useState(false)
  const [openTermsAndConditions, setOpenTermsAndConditions] = useState(false)
  const handleTermsAnConditionsOpen = () => {
    setOpenTermsAndConditions(true)
  }
  const handleTermsAnConditionsClose = () => {
    setOpenTermsAndConditions(false)
  }
  const {
    id: cloudAccountId,
    status: cloudAccountStatus,
    ISV,
    grantConsent,
    terms,
    MPNID,
    name,
    industries,
    microsoftPartnerType,
    fetchedForPartner,
  } = useSelector(
    (state: CloudSettingsState) =>
      state.accountForm[productType].accountDetails.AZURE
  )

  const [nameError, setNameError] = useState<string>('')
  const [MPNIDError, setMPNIDError] = useState<boolean>(false)

  const partnerId = useSelector(
    (state: CloudSettingsState) =>
      state.AccountData[partnerType].partnerData?.partnerId as string | undefined
  )

  const loading = useSelector((state: CloudSettingsState) =>
    isLoading(state.loading)(LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS)
  )
  const isAzureAlreadyConnected =
    ![
      MPNID,
      name,
      industries,
      microsoftPartnerType,
      fetchedForPartner,
      cloudAccountId,
    ].some(isEmpty) &&
    partnerId === fetchedForPartner &&
    cloudAccountStatus === 'active'

  useEffect(() => {
    if (
      !isAzureAlreadyConnected &&
      !loading &&
      !!partnerId &&
      partnerId !== fetchedForPartner &&
      isAzureFlagEnabled
    ) {
      // if any of those fields are empty then make a request to the backend
      dispatch(getAzureAccountDetails(partnerId, productType))
    }
  }, [partnerId, productType])

  const onChangeCheckBoxValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target
      dispatch(
        actionTypeWrapper(
          productType,
          updateValueOnAccountDetails(name, checked)
        )
      )
    },
    []
  )
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target
      handleErrors(name, value)
      dispatch(
        actionTypeWrapper(productType, updateValueOnAccountDetails(name, value))
      )
    },
    []
  )

  const handleErrors = (key: string, value: unknown) => {
    if (key === 'MPNID') {
      isEmpty(value) ? setMPNIDError(true) : setMPNIDError(false)
    }
    if (key === 'name') {
      const strValidated = validateString(
        value as string,
        'Microsoft Partner Name'
      )
      setNameError(strValidated)
    }
  }
  const handleInputChangeSelect = useCallback((event: unknown) => {
    const { name, value } = (event as React.ChangeEvent<HTMLInputElement>)
      .target
    dispatch(
      actionTypeWrapper(productType, updateValueOnAccountDetails(name, value))
    )
  }, [])

  const handleMicrosoftPartnerTypeSelection = useCallback(
    (_event: React.ChangeEvent<object>, newValues: string[]) => {
      dispatch(
        actionTypeWrapper(
          productType,
          updateValueOnAccountDetails('microsoftPartnerType', newValues)
        )
      )
    },
    []
  )

  const handleDisconnectAzure = () => {
    dispatch(
      disconnectAzureAccount(partnerId || '', productType, cloudAccountId || '')
    )
    setOpenDisconnectModal(false)
  }

  const enabled =
    grantConsent &&
    terms &&
    !isEmpty(MPNID) &&
    !isEmpty(name) &&
    !isEmpty(industries) &&
    !isEmpty(microsoftPartnerType) &&
    isEmpty(nameError) &&
    !MPNIDError

  if (!partnerId || loading) {
    // this is just a safeguard to not error out, but it should not happen
    return <></>
  }
  const fieldSelectData = {
    ...formFields.select,
    defaultValue: industries,
    isReadOnly: isAzureAlreadyConnected,
    isRequired: false,
  }

  const multiSelectData = {
    ...formFields.multiselect,
    isReadOnly: isAzureAlreadyConnected,
  }

  return (
    <div className={classes.content} data-testid="azure-cloud-registration">
      <Grid container spacing={4}>
        {' '}
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 9,
            lg: 9,
            xl: 9,
          }}
        >
          <AutoCompleteWithMultiSelect
            id={multiSelectData.id}
            label={multiSelectData.title}
            options={multiSelectData.options}
            onChange={handleMicrosoftPartnerTypeSelection}
            value={microsoftPartnerType || []}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4} className={classes.grid}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
            lg: 3,
            xl: 3,
          }}
        >
          <SelectV2
            field={fieldSelectData}
            onChange={handleInputChangeSelect}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
            lg: 3,
            xl: 3,
          }}
        >
          <TextInputV2
            type={'number'}
            keyName={'MPNID'}
            isReadOnly={isAzureAlreadyConnected}
            title={'MPN ID'}
            isRequired={false}
            value={MPNID || ''}
            placeHolder={'Enter MPN ID'}
            onChange={handleInputChange}
            isError={MPNIDError}
            errorText={MPNIDError ? 'MPN ID is required' : ''}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
            lg: 3,
            xl: 3,
          }}
        >
          <TextInputV2
            keyName={'name'}
            isReadOnly={isAzureAlreadyConnected}
            title={'Microsoft partner name'}
            isRequired={false}
            value={name || ''}
            placeHolder={'Enter Microsoft Partner Name'}
            onChange={handleInputChange}
            isError={!isEmpty(nameError)}
            errorText={!isEmpty(nameError) ? nameError : ''}
          />
        </Grid>
      </Grid>
      <div>
        <CheckBoxFieldv2
          keyName={'ISV'}
          title={'I am an Independent Software Vendor'}
          value={ISV}
          onChangeBox={onChangeCheckBoxValue}
          disabled={isAzureAlreadyConnected}
        />
      </div>
      <div>
        {' '}
        <CheckBoxFieldv2
          keyName={'grantConsent'}
          title={'Grant consent to Labra to access Microsoft Partner Center'}
          value={grantConsent}
          isRequired={true}
          onChangeBox={onChangeCheckBoxValue}
          disabled={isAzureAlreadyConnected}
        />
      </div>
      <div>
        <DialogComponent
          open={openTermsAndConditions}
          handleClose={handleTermsAnConditionsClose}
        />{' '}
        <CheckBoxFieldv2
          keyName={'terms'}
          title={
            <>
              I accept
              <a
                onClick={handleTermsAnConditionsOpen}
                className={classes.linkText}
              >
                terms & conditions
              </a>
            </>
          }
          value={terms}
          isRequired={true}
          onChangeBox={onChangeCheckBoxValue}
          disabled={isAzureAlreadyConnected}
        />
      </div>
      <div className={classes.azureButton}>
        {isAzureAlreadyConnected ? (
          <ButtonV2 onClick={() => setOpenDisconnectModal(true)}>
            Disconnect Microsoft Partner Center
          </ButtonV2>
        ) : (
          <AzurePopupConnection
            productType={productType}
            enabled={enabled}
            partnerType={partnerType}
          />
        )}
      </div>
      <ModalConfirmV2
        title={'Disconnect Microsoft Partner Center'}
        description={
          'Please confirm to disconnect your Microsoft Partner Center account'
        }
        open={openDisconnectModal}
        titleAcceptButton={'Disconnect'}
        titleCancelButton={'Cancel'}
        onAccept={handleDisconnectAzure}
        onDecline={() => setOpenDisconnectModal(false)}
        variant={ModalSizeVariants.large}
      />
    </div>
  )
}
